/*
 * cluutch-pdp-2026.js
 * Module de la fiche produit 2026 : des custom elements auto-initialisés en connectedCallback,
 * parce que le Section Rendering et l'éditeur de thème réinjectent des morceaux de section.
 * Chargé en `<script type="module">` par sections/cluutch-pdp.liquid, donc différé.
 *
 * Événement pivot : `product:variant-change`, émis sur `document` par product.js à chaque
 * changement de teinte, avec `{ variant, sectionId }`. Ce module ne fait que suivre : l'état
 * initial est rendu en Liquid.
 *
 * Deux custom elements :
 *   - `<cluutch-pdp-galerie>` : le rail à défilement aimanté, ses vignettes, son compteur, ses
 *     chevrons, la loupe (`<dialog>`), les vidéos à la demande, et la lame de la teinte choisie
 *     amenée dans le rail (jamais la fenêtre : `scrollTo` sur le rail seul) ;
 *   - `<cluutch-pdp-achat>` : le moteur d'achat. État `{ variante, crayon, gloss }`, total
 *     calculé depuis les prix Shopify embarqués par cluutch-pdp-achat-donnees.liquid, écrit en
 *     `data-cluutch-total` (centimes) sur `<product-form>` et diffusé par `cluutch:pdp-total`
 *     pour cluutch-atc-price.js et cluutch-installments.js ; la jauge, la ligne de cadeau et
 *     l'encart de bascule suivent (la jauge et la ligne de cadeau comptent aussi le panier, relu
 *     à l'arrivée et à chaque re-rendu du tiroir : chantier ⑮) ; le nom de la teinte et le crayon
 *     assorti suivent la teinte.
 *     Ajout au panier : une ligne, le `<product-form>` natif fait tout ; deux lignes ou la
 *     variante du coffret, le moteur capture le `submit` sur `document` (phase de capture,
 *     avant l'écouteur que product.js pose sur le formulaire), poste les `items` en JSON sur
 *     `theme.routes.cart_add_url` avec les mêmes `sections`, puis appelle
 *     `productForm.renderContents(json)` (méthode publique) et émet `cart:item-added`, comme
 *     product.js. Le moteur n'écrit JAMAIS dans `input[name="id"]` : product.js le réécrit à
 *     chaque changement de teinte ; les lignes se composent au `submit`, depuis cet input.
 *
 * Lot 4 : `<cluutch-pdp-ancres>` (suivi de lecture, pastille glissante depuis le lot 11), `<cluutch-pdp-teintes>` (rail lié à
 * la teinte dans les deux sens), `<cluutch-pdp-compare>` (avant / après), `<cluutch-pdp-avis>`
 * (widget déplié, loupe des photos). Lot 5, le mouvement (§G) : les révélations des sections
 * basses (`[data-cluutch-reveal]`, un seul IntersectionObserver, 160 ms, aucune cascade),
 * l'amorce des rails (`[data-cluutch-amorce]` : le rail avance de 40 % d'une lame et revient,
 * une fois, 980 ms, coupée au geste et au changement de teinte ; ce que les recommandations
 * différées injectent est observé à son tour), la vidéo de L'actif jouée à l'écran ; tout est
 * coupé sous prefers-reduced-motion (le CSS aussi : `html.cluutch-pdp-anime` n'est posé que sans
 * elle).
 * Lot 9, les coffrets : la galerie en mode `variante` échange sa lame 0 au changement de
 * variante (préchargement au même `srcset` / `sizes`), le moteur joint les valeurs d'option par
 * « · », tient les légendes d'option (`[data-cluutch-nom-option]`) et le prix barré du flottant
 * (clé `barre`) ; `<cluutch-pdp-composition>` (« Ce coffret contient ») met ses lignes et son
 * pied à jour depuis la table JSON embarquée.
 * Habillage de collection (Hôtel Charly, 06/10/2026) : quand la teinte choisie est habillée, la
 * galerie (mode `toutes`) met en tête son packshot et ses médias marqués, et la rend dans l'ordre
 * produit quand on la quitte ; `<cluutch-pdp-habillage>` tient l'attribut et l'accent sur
 * `<html>` et l'encart. Rien de cela n'existe sur une fiche sans teinte habillée visible : les
 * attributs et l'élément ne sont pas rendus.
 * Teinte en tête (30/10/2026) : sur une fiche repérée (un média marqué `#couleur_`), la galerie
 * fait de même pour toutes les teintes, habillées ou non ; l'habillage reste aux teintes habillées.
 * Auteur : Cluutch / Studio Hogo.
 */
// Garde d'idempotence : la section peut être réinjectée (éditeur, Section Rendering) et le
// module redemandé ; tout ce qui suit ne s'exécute qu'une fois.
if (!window.cluutchPdpReady) {
  window.cluutchPdpReady = true;

  // « Produits vus récemment » : product.js appelle addIdToRecentlyViewed() sans argument en fin
  // de chargement, et app.js lit alors le handle sur `.thb-product-detail`, que cette fiche ne
  // rend pas (voulu : cet ancêtre déclenche la galerie native). Sans cet appel, la fiche 2026
  // n'alimenterait plus le rail du panier ni celui des autres fiches. app.js est chargé en
  // synchrone avant ce module : la fonction existe.
  var fiche = document.querySelector('.cluutch-pdp[data-handle]');
  if (fiche && typeof window.addIdToRecentlyViewed === 'function') {
    window.addIdToRecentlyViewed(fiche.dataset.handle);
  }

  var mouvementReduit = function () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  // Montants : le format du thème (shop.money_format), le même que le filtre `money`.
  var formatArgent = function (centimes) {
    if (typeof formatMoney !== 'function') return String(centimes / 100);
    var format = (window.theme && window.theme.settings && window.theme.settings.money_with_currency_format) || '{{amount}}';
    return formatMoney(centimes, format);
  };
  // « 18 € » plutôt que « 18,00 € » (jauge, paliers), comme `money_without_trailing_zeros`.
  var formatArgentCourt = function (centimes) {
    return formatArgent(centimes).replace(/[.,]00(?=\D*$)/, '');
  };
  var remplir = function (modele, valeurs) {
    return Object.keys(valeurs).reduce(function (s, cle) {
      return s.split('[' + cle + ']').join(valeurs[cle]);
    }, modele || '');
  };
  var echapper = function (s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  };
  var normaliser = function (s) {
    return String(s || '').toLowerCase().trim().replace(/\s+/g, ' ');
  };
  // La remise d'un coffret en pourcentage, « −9 % », arrondie au pourcent le plus proche
  // (décision du 21/09 : le pourcentage remplace les euros d'économie) ; '' sans prix barré
  // supérieur. Le texte vient de la locale (`cluutch_pdp.remise`), le nombre est calculé.
  var formatRemise = function (modele, prix, barre) {
    if (!(barre > prix) || !modele) return '';
    // (barre − prix) × 100 / barre, dans cet ordre : celui de Liquid (`minus | times: 100.0 |
    // divided_by`), sinon un demi-pourcent exact s'arrondit différemment ici et là (audit du
    // lot B) ; moins de 1 %, rien (« −0 % » ne se dit pas).
    var pourcent = Math.round(((barre - prix) * 100) / barre);
    return pourcent >= 1 ? remplir(modele, { pourcent: pourcent }) : '';
  };
  // La minuscule initiale d'un nom court (« Son gloss » → « son gloss ») quand il entre dans
  // une phrase (encart, annonces).
  var minuscule = function (s) {
    s = String(s || '');
    return s.charAt(0).toLowerCase() + s.slice(1);
  };
  // Un texte remplacé en fondu de 160 ms (classe `est-en-fondu`), sec sous mouvement réduit.
  // La cible en attente est mémorisée sur l'élément et le fondu précédent annulé : un
  // aller-retour de teinte en moins de 160 ms comparerait sinon au DOM en retard et laisserait
  // le titre intermédiaire s'écrire après coup (audit du lot A).
  var fondre = function (el, texte) {
    if (el.cluutchFondu) { window.clearTimeout(el.cluutchFondu); el.cluutchFondu = null; }
    if (mouvementReduit()) { el.textContent = texte; el.cluutchCible = null; return; }
    el.cluutchCible = texte;
    el.classList.add('est-en-fondu');
    el.cluutchFondu = window.setTimeout(function () {
      el.textContent = texte;
      el.classList.remove('est-en-fondu');
      el.cluutchFondu = null;
      el.cluutchCible = null;
    }, 160);
  };
  // Le texte qu'un élément affichera une fois son fondu fini (ou affiche déjà).
  var texteVise = function (el) {
    return el.cluutchCible != null ? el.cluutchCible : el.textContent.trim();
  };

  /* ------------------------------------------------------------------ la galerie */
  if (!customElements.get('cluutch-pdp-galerie')) {
    class CluutchPdpGalerie extends HTMLElement {
      connectedCallback() {
        this.rail = this.querySelector('[data-cluutch-rail]');
        this.lames = this.rail ? Array.prototype.slice.call(this.rail.children) : [];
        this.vignettes = Array.prototype.slice.call(this.querySelectorAll('[data-cluutch-vignette]'));
        this.compteur = this.querySelector('[data-cluutch-compteur]');
        this.chevrons = Array.prototype.slice.call(this.querySelectorAll('[data-cluutch-chevron]'));
        this.loupe = this.querySelector('[data-cluutch-loupe]');
        this.zoom = this.querySelector('[data-cluutch-zoom]');
        this.verrou = 0;
        this.index = 0;
        // Mode « la variante + les communs » (les coffrets, lot 9) : la lame 0 est échangée au
        // changement de variante, aux largeurs et au `sizes` que Liquid a échoés (les mêmes que
        // la lame rendue : cluutch-pdp-galerie-image en est la seule source).
        this.parVariante = this.dataset.cluutchGalerie === 'variante';
        this.largeurs = (this.dataset.cluutchLargeurs || '').split(',').map(function (l) { return parseInt(l, 10); }).filter(Boolean);
        this.tailles = this.dataset.cluutchTailles || '';
        this.echange = null;
        // Habillage de collection : `data-cluutch-habillage-teintes` (« id:teinte,… », les
        // variantes visibles habillées, ou toutes sur une fiche repérée) n'est rendu que sur une
        // fiche qui en a, en mode `toutes` ;
        // `data-cluutch-habillage-tete` : la teinte dont Liquid a déjà mis les médias en tête.
        this.teintes = null;
        this.enTete = '';
        if (this.dataset.cluutchHabillageTeintes) {
          this.teintes = {};
          this.dataset.cluutchHabillageTeintes.split(',').forEach(function (paire) {
            var p = paire.split(':');
            if (p[0] && p[1]) this.teintes[p[0]] = p[1];
          }, this);
          this.enTete = this.dataset.cluutchHabillageTete || '';
        }

        this.surVariante = this.surVariante.bind(this);
        this.surDefilement = this.surDefilement.bind(this);
        document.addEventListener('product:variant-change', this.surVariante);
        if (this.rail) {
          this.rail.addEventListener('scroll', this.surDefilement, { passive: true });
          this.rail.addEventListener('keydown', this.surClavier.bind(this));
        }
        this.vignettes.forEach(function (v) {
          v.addEventListener('click', function () {
            var lame = this.rail.querySelector('[data-cluutch-media="' + v.dataset.cluutchVignette + '"]');
            if (lame) this.aller(this.lames.indexOf(lame));
          }.bind(this));
        }, this);
        this.chevrons.forEach(function (c) {
          c.addEventListener('click', function () {
            this.aller(this.index + parseInt(c.dataset.cluutchChevron, 10));
          }.bind(this));
        }, this);
        this.querySelectorAll('[data-cluutch-lire]').forEach(function (bouton) {
          bouton.addEventListener('click', this.lireVideo.bind(this, bouton));
        }, this);
        if (this.loupe && this.zoom) {
          this.loupe.addEventListener('click', this.ouvrirLoupe.bind(this));
          var fermer = this.zoom.querySelector('[data-cluutch-zoom-fermer]');
          if (fermer) fermer.addEventListener('click', function () { this.zoom.close(); }.bind(this));
          this.zoom.addEventListener('click', function (e) { if (e.target === this.zoom) this.zoom.close(); }.bind(this));
        }
        this.marquer(0);
      }

      disconnectedCallback() {
        document.removeEventListener('product:variant-change', this.surVariante);
      }

      // Le retrait gauche du rail (sa gouttière) : là où le défilement aimanté pose une lame.
      retrait() {
        return parseFloat(window.getComputedStyle(this.rail).paddingLeft) || 0;
      }

      // La lame la plus proche de la position de défilement (la dernière lame d'ordinateur ne
      // peut pas se caler au bord : le rail s'arrête avant, le plus proche décide).
      courant() {
        var x = this.rail.scrollLeft + this.retrait();
        var meilleur = 0, distance = Infinity;
        this.lames.forEach(function (lame, i) {
          var d = Math.abs(lame.offsetLeft - x);
          if (d < distance) { distance = d; meilleur = i; }
        });
        return meilleur;
      }

      aller(i, comportement) {
        if (!this.rail || !this.lames.length) return;
        i = Math.max(0, Math.min(this.lames.length - 1, i));
        // Verrou de 700 ms après un clic de vignette : le défilement doux passe par des lames
        // intermédiaires, la vignette courante ne doit pas clignoter.
        this.verrou = Date.now() + 700;
        this.marquer(i);
        this.rail.scrollTo({
          left: this.lames[i].offsetLeft - this.retrait(),
          behavior: comportement || (mouvementReduit() ? 'auto' : 'smooth')
        });
      }

      surDefilement() {
        if (this.cadre) return;
        this.cadre = window.requestAnimationFrame(function () {
          this.cadre = 0;
          if (Date.now() < this.verrou) return;
          this.marquer(this.courant());
        }.bind(this));
      }

      surClavier(e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.aller(this.index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.aller(this.index - 1); }
      }

      // Compteur, vignette courante, chevrons aux extrémités, vidéos hors champ en pause.
      marquer(i) {
        this.index = i;
        var lame = this.lames[i];
        if (this.compteur) this.compteur.textContent = (i + 1) + '/' + this.lames.length;
        var id = lame ? lame.dataset.cluutchMedia : null;
        this.vignettes.forEach(function (v) {
          var courante = v.dataset.cluutchVignette === id;
          if (courante) v.setAttribute('aria-current', 'true'); else v.removeAttribute('aria-current');
          v.setAttribute('data-cluutch-spec', courante ? 'vignette-galerie' : 'vignette-galerie-inactive');
        });
        this.chevrons.forEach(function (c) {
          var sens = parseInt(c.dataset.cluutchChevron, 10);
          c.hidden = sens < 0 ? i === 0 : i >= this.lames.length - 1;
        }, this);
        // La loupe agrandit une image : sur une lame vidéo elle se retire.
        if (this.loupe) this.loupe.hidden = !!(lame && lame.querySelector('[data-cluutch-lire]'));
        this.lames.forEach(function (autre, j) {
          if (j === i) return;
          var video = autre.querySelector('video');
          if (video && !video.paused) video.pause();
        });
        var vignette = this.vignettes[i];
        if (vignette && vignette.parentElement) {
          var bande = vignette.parentElement;
          var gauche = vignette.offsetLeft - (parseFloat(window.getComputedStyle(bande).paddingLeft) || 0);
          if (gauche < bande.scrollLeft || vignette.offsetLeft + vignette.offsetWidth > bande.scrollLeft + bande.clientWidth) {
            bande.scrollTo({ left: gauche, behavior: mouvementReduit() ? 'auto' : 'smooth' });
          }
        }
      }

      lireVideo(bouton) {
        var conteneur = bouton.nextElementSibling;
        var video = conteneur ? conteneur.querySelector('video') : null;
        if (!video) return;
        bouton.hidden = true;
        conteneur.hidden = false;
        video.play().catch(function () {});
      }

      ouvrirLoupe() {
        var lame = this.lames[this.index];
        var img = lame ? lame.querySelector('img') : null;
        var cible = this.zoom.querySelector('[data-cluutch-zoom-image]');
        if (!img || !cible) return;
        // La plus grande candidate du srcset (1600), pas celle que le rail a chargée.
        var candidates = (img.getAttribute('srcset') || '').split(',');
        var derniere = candidates.length ? candidates[candidates.length - 1].trim().split(/\s+/)[0] : '';
        cible.src = derniere || img.currentSrc || img.src;
        cible.alt = img.alt || '';
        this.zoom.showModal();
      }

      surVariante(e) {
        // product.js émet `variant: undefined` quand aucune variante ne correspond aux options
        // choisies : on l'ignore.
        if (!e.detail || !e.detail.variant) return;
        // L'aperçu rapide d'un autre produit, ou d'un produit du tiroir, émet le même événement
        // avec le `data-section` de son propre sélecteur : seul celui de cette section compte.
        if (e.detail.sectionId !== this.dataset.section) return;
        if (!this.rail) return;

        // Une variante sans média mis en avant, ou dont le média n'est pas une lame, ne déplace
        // pas le rail. Le rail défile, jamais la fenêtre.
        var media = e.detail.variant.featured_media;
        var lame = media && media.id ? this.rail.querySelector('[data-cluutch-media="' + media.id + '"]') : null;
        // Tout échange en vol est annulé dès qu'un événement de la section n'en demande pas un :
        // A → B → A avant la fin du décodage de B laissait B se poser sur la lame de A (audit).
        this.echange = null;
        // Une teinte habillée ouvre la galerie sur ses visuels, la quitter rend l'ordre produit.
        // Pour elle, le rail revient à la lame 0 sans animation, À LA PLACE du recalage sur le
        // packshot (un `scrollTo` 'auto' coupe le défilement doux en cours).
        if (this.teintes) {
          var teinte = this.teintes[String(e.detail.variant.id)] || '';
          if (teinte || this.enTete) this.ordonner(teinte, media && media.id ? String(media.id) : '');
          if (teinte) { this.aller(0, 'auto'); return; }
        }
        if (lame) { this.aller(this.lames.indexOf(lame)); return; }
        if (this.parVariante && media && media.id && media.preview_image) this.echangerLame(media);
      }

      // Habillage de collection : lames et vignettes dans le même ordre. Pour une teinte habillée,
      // son packshot, ses médias marqués (`data-cluutch-couleur`), puis le rang produit
      // (`data-cluutch-rang`, posé par Liquid) ; sans teinte, le rang seul. Les tableaux sont
      // relus : compteur, chevrons, vignette courante et loupe suivent l'index.
      ordonner(teinte, packshot) {
        var cle = function (el, id) {
          var groupe = !teinte ? 2 : id === packshot ? 0 : el.dataset.cluutchCouleur === teinte ? 1 : 2;
          return groupe * 1e6 + (parseInt(el.dataset.cluutchRang, 10) || 0);
        };
        var trier = function (parent, attribut) {
          if (!parent) return;
          Array.prototype.slice.call(parent.children)
            .sort(function (a, b) { return cle(a, a.dataset[attribut]) - cle(b, b.dataset[attribut]); })
            .forEach(function (el) { parent.appendChild(el); });
        };
        trier(this.rail, 'cluutchMedia');
        trier(this.querySelector('[data-cluutch-vignettes]'), 'cluutchVignette');
        this.lames = Array.prototype.slice.call(this.rail.children);
        this.vignettes = Array.prototype.slice.call(this.querySelectorAll('[data-cluutch-vignette]'));
        this.enTete = teinte;
      }

      // Mode variante : le média de la variante n'est pas une lame, il PREND la lame 0 (et la
      // vignette 0). Préchargement au même `srcset` et au même `sizes` que la lame (jamais `src`
      // seul : le navigateur choisirait un autre candidat et téléchargerait deux fois), puis
      // `src`, `srcset`, `alt`, `data-cluutch-media`, et le rail revient à la lame 0. Un
      // changement qui en rattrape un autre pendant le décodage annule le premier.
      echangerLame(media) {
        var lame = this.lames[0];
        var img = lame ? lame.querySelector('img') : null;
        if (!img || lame.querySelector('[data-cluutch-lire]')) return;
        var apercu = media.preview_image;
        var base = apercu.src;
        var sep = base.indexOf('?') >= 0 ? '&' : '?';
        var largeurs = this.largeurs.filter(function (l) { return !apercu.width || l <= apercu.width; });
        if (!largeurs.length) largeurs = [this.largeurs[0] || 335];
        var srcset = largeurs.map(function (l) { return base + sep + 'width=' + l + ' ' + l + 'w'; }).join(', ');
        var src = base + sep + 'width=' + (this.largeurs[this.largeurs.length - 1] || largeurs[largeurs.length - 1]);
        var alt = String(media.alt || '').split('#')[0].trim() || img.alt;
        var jeton = String(media.id);
        this.echange = jeton;
        var poser = function () {
          if (this.echange !== jeton) return;
          this.echange = null;
          img.srcset = srcset;
          if (this.tailles) img.sizes = this.tailles;
          img.src = src;
          img.alt = alt;
          if (apercu.width && apercu.height) { img.width = apercu.width; img.height = apercu.height; }
          lame.dataset.cluutchMedia = jeton;
          var vignette = this.vignettes[0];
          if (vignette) {
            vignette.dataset.cluutchVignette = jeton;
            vignette.setAttribute('aria-label', alt);
            var mini = vignette.querySelector('img');
            if (mini) { mini.srcset = base + sep + 'width=108 108w, ' + base + sep + 'width=176 176w'; mini.src = base + sep + 'width=176'; }
          }
          this.aller(0, 'auto');
        }.bind(this);
        var pre = new Image();
        pre.sizes = this.tailles || img.sizes || '';
        pre.srcset = srcset;
        pre.src = src;
        var decodage = typeof pre.decode === 'function' ? pre.decode() : Promise.resolve();
        decodage.then(poser, poser);
      }
    }

    customElements.define('cluutch-pdp-galerie', CluutchPdpGalerie);
  }

  /* ------------------------------------------------------------------ le moteur d'achat */
  if (!customElements.get('cluutch-pdp-achat')) {
    class CluutchPdpAchat extends HTMLElement {
      connectedCallback() {
        var brut = this.querySelector('[data-cluutch-pdp-donnees]');
        try {
          this.donnees = brut ? JSON.parse(brut.textContent) : null;
        } catch (err) {
          this.donnees = null;
        }
        if (!this.donnees) return;
        this.sectionId = this.dataset.section;
        // La racine de la fiche : l'achat flottant (lot 3) vit hors de la colonne, dans la section.
        this.racine = this.closest('.cluutch-pdp') || this;
        this.formId = 'product-form-' + this.sectionId;
        this.form = document.getElementById(this.formId);
        this.productForm = this.form ? this.form.closest('product-form') : null;
        this.statut = this.querySelector('[data-cluutch-statut]');
        this.textes = this.donnees.textes || {};
        // Le panier : rendu par Liquid, relu ensuite (rafraichirPanier). La jauge part du panier et
        // n'y ajoute la sélection qu'une fois une case cochée (chantier ⑯).
        this.panier = Number(this.donnees.panier) || 0;
        this.selectionAjoutee = false;
        // Les cases : une par article cochable (`data-cluutch-addon="1|2"`), génériques (lot A du
        // chantier ⑬ : plus de clé crayon ni gloss).
        this.cases = {};
        this.querySelectorAll('[data-cluutch-addon]').forEach(function (label) {
          var cle = label.dataset.cluutchAddon;
          var input = label.querySelector('[data-cluutch-addon-case]');
          if (!input) return;
          this.cases[cle] = {
            label: label, input: input,
            titre: label.querySelector('[data-cluutch-addon-titre]'),
            detail: label.querySelector('[data-cluutch-addon-detail]'),
            notes: label.querySelectorAll('[data-cluutch-addon-note]'),
            image: label.querySelector('.cluutch-pdp__addon-image'),
            visuel: label.querySelector('.cluutch-pdp__addon-visuel'),
            prix: label.querySelector('[data-cluutch-addon-prix]'),
            tag: label.querySelector('[data-cluutch-addon-remise]')
          };
          input.addEventListener('change', this.surCase.bind(this, cle));
        }, this);

        this.surVariante = this.surVariante.bind(this);
        this.surSoumission = this.surSoumission.bind(this);
        this.surAjout = this.surAjout.bind(this);
        this.surEchec = this.surEchec.bind(this);
        document.addEventListener('product:variant-change', this.surVariante);
        document.addEventListener('submit', this.surSoumission, true);
        document.addEventListener('cart:item-added', this.surAjout);
        document.addEventListener('product:variant-error', this.surEchec);
        // Relire le panier : chaque changement re-rend les enfants directs du tiroir (ajout,
        // quantité, ligne retirée, « Tout supprimer », code promo ; ces deux derniers n'émettent
        // aucun événement), et une page restaurée par le retour arrière garde son ancien panier.
        this.rafraichirPanier = this.rafraichirPanier.bind(this);
        this.planifierPanier = this.planifierPanier.bind(this);
        this.surRetour = this.surRetour.bind(this);
        var tiroir = document.getElementById('Cart-Drawer');
        if (tiroir) {
          this.observateurPanier = new MutationObserver(this.planifierPanier);
          this.observateurPanier.observe(tiroir, { childList: true });
        }
        window.addEventListener('pageshow', this.surRetour);

        this.marquerTeintesClaires();
        this.varianteId = this.idCourant();
        this.recalculer();
        // Une fiche préchargée au survol (instant.page) peut arriver avec un panier ancien.
        this.rafraichirPanier();
      }

      disconnectedCallback() {
        document.removeEventListener('product:variant-change', this.surVariante);
        document.removeEventListener('submit', this.surSoumission, true);
        document.removeEventListener('cart:item-added', this.surAjout);
        document.removeEventListener('product:variant-error', this.surEchec);
        window.removeEventListener('pageshow', this.surRetour);
        if (this.observateurPanier) this.observateurPanier.disconnect();
        window.clearTimeout(this.minuteriePanier);
      }

      // La variante choisie : celle de l'input du formulaire, que product.js tient à jour.
      idCourant() {
        var input = this.form ? this.form.querySelector('input[name="id"]') : null;
        return input ? String(input.value) : null;
      }

      variante() {
        return this.donnees.variantes[this.varianteId] || null;
      }

      // La coche sur une teinte claire est noire : luminance du rond de chaque cellule, lue une
      // fois (le sélecteur n'est pas re-rendu par le Section Rendering).
      marquerTeintesClaires() {
        this.querySelectorAll('.product-form__input--color input[type="radio"] + label').forEach(function (label) {
          var couleur = window.getComputedStyle(label, '::after').backgroundColor || '';
          var m = couleur.match(/\d+(\.\d+)?/g);
          if (!m || m.length < 3) return;
          var lum = (0.2126 * m[0] + 0.7152 * m[1] + 0.0722 * m[2]) / 255;
          label.classList.toggle('est-claire', lum > 0.6);
        });
      }

      surVariante(e) {
        if (!e.detail || !e.detail.variant) return;
        if (e.detail.sectionId !== this.sectionId) return;
        var v = e.detail.variant;
        this.varianteId = String(v.id);
        this.selectionAjoutee = false;
        // Les valeurs d'option jointes par « · » : identique à une option (le rouge), « Red kiss ·
        // Berry » sur un coffret à deux options (lot 9 ; la même jointure que achat-donnees).
        var teinte = [v.option1, v.option2, v.option3].filter(Boolean).join(' · ');
        // Le nom de la teinte, en fondu (ligne de teinte, titre du crayon, achat flottant), et la
        // valeur de chaque légende d'option (cluutch-pdp-legende, coffrets).
        this.racine.querySelectorAll('[data-cluutch-nom-teinte]').forEach(function (el) { fondre(el, teinte); });
        this.racine.querySelectorAll('[data-cluutch-nom-option]').forEach(function (el) {
          fondre(el, v['option' + el.dataset.cluutchNomOption] || '');
        });
        var stock = this.querySelector('[data-cluutch-stock]');
        if (stock) stock.hidden = !v.available;
        // La vignette de l'achat flottant (lot 3) suit la teinte tout de suite, depuis l'image de
        // la variante que product.js porte dans l'événement : le Section Rendering la remplacera
        // par la même, quand il arrivera (mesuré à 14 s quand les vidéos « en action » se
        // chargent sur la même connexion).
        var vignette = this.racine.querySelector('#product-image-' + this.sectionId + '--sticky img');
        if (vignette && v.featured_image && v.featured_image.src) {
          var base = v.featured_image.src;
          var sep = base.indexOf('?') >= 0 ? '&' : '?';
          vignette.srcset = base + sep + 'width=96 96w, ' + base + sep + 'width=192 192w';
          vignette.src = base + sep + 'width=96';
        }
        // La mini-cellule cochée du flottant reste à la vue : sa rangée défile au-delà de cinq
        // teintes (treize sur le crayon). product.js coche la radio (updateOther) avant cet
        // événement ; le rail est recalé sur elle, jamais la fenêtre (scrollTo sur le rail seul).
        var cellules = this.racine.querySelector('.cluutch-pdp__flottant-cellules');
        if (cellules && cellules.scrollWidth > cellules.clientWidth + 1) {
          var cochee = cellules.querySelector('input[type="radio"]:checked + label');
          if (cochee) {
            var gauche = cochee.offsetLeft - cellules.offsetLeft;
            if (gauche < cellules.scrollLeft || gauche + cochee.offsetWidth > cellules.scrollLeft + cellules.clientWidth) {
              cellules.scrollTo({ left: Math.max(0, gauche - (cellules.clientWidth - cochee.offsetWidth) / 2), behavior: douceur() });
            }
          }
        }
        // Épuisé : par classe, pas par `[disabled]` que product.js pose aussi le temps du Section
        // Rendering ; `sold-out` (Liquid) n'est jamais retiré par product.js, on le tient à jour.
        // Le libellé aussi : product.js ne l'écrit qu'à la résolution du fetch de section (jusqu'à
        // 14 s mesurées), le bouton dirait « Ajouter au panier » en gris jusque-là. Mêmes chaînes
        // que product.js (theme.variantStrings), qu'il réécrira à l'identique.
        var chaines = (window.theme && window.theme.variantStrings) || {};
        var forme = this.racine.querySelector('.product-form');
        var precommande = forme && (forme.getAttribute('template') || '').indexOf('pre-order') >= 0;
        var libelle = !v.available ? chaines.soldOut : (precommande ? chaines.preOrder : chaines.addToCart);
        document.querySelectorAll('.cluutch-pdp .single-add-to-cart-button').forEach(function (b) {
          b.classList.toggle('est-epuise', !v.available);
          b.classList.toggle('sold-out', !v.available);
          var texte = b.querySelector('.single-add-to-cart-button--text');
          if (texte && libelle && !b.classList.contains('est-ajoute')) texte.textContent = libelle;
        });
        this.recalculer();
        // L'annonce d'un cochable épuisé décoché par recalculer() suit celle de la teinte, dans
        // la même phrase : deux annoncer() de suite, la seconde effacerait la première.
        this.annoncer([remplir(this.textes.annonce_teinte, { teinte: teinte })].concat(this.annoncesEpuise || []).join(' '));
      }

      surCase(cle) {
        this.selectionAjoutee = false;
        this.recalculer();
        var etat = this.etat();
        var cellule = this.cases[cle];
        var coche = cellule.input.checked;
        var proposition = this.resoudre(cle);
        var article = proposition ? minuscule(proposition.nom) : cle;
        var offert = '';
        var projection = this.projection(etat);
        if (this.donnees.seuils.cadeau > 0 && projection >= this.donnees.seuils.cadeau) offert = this.textes.offert_cadeau || '';
        else if (projection >= this.donnees.seuils.livraison) offert = this.textes.offert_livraison || '';
        this.annoncer(remplir(coche ? this.textes.annonce_ajoute : this.textes.annonce_retire,
          { article: article, total: formatArgent(etat.total), offert: offert }));
      }

      /* Le moteur généralisé (chantier ⑬, lot A). Deux articles cochables, chacun avec une
         proposition de variante résolue depuis les données (cluutch-pdp-achat-donnees) : par
         variante de la fiche quand elle est assortie, sinon le défaut du produit. La bascule sur
         le coffret est dérivée de sa structure (`options`, `fixes`, `variantes`) : elle
         s'applique quand la fiche et un sous-ensemble des cochés en sont exactement les
         composants ; le reste s'ajoute à l'unité. */
      resoudre(cle) {
        var c = this.donnees.cochables ? this.donnees.cochables[cle] : null;
        if (!c) return null;
        var par = c.parVariante && this.varianteId ? c.parVariante[this.varianteId] : null;
        return par || c.defaut || null;
      }

      // Les articles cochés, résolus et disponibles : [{cle, proposition}].
      coches() {
        var out = [];
        Object.keys(this.cases).forEach(function (cle) {
          var cellule = this.cases[cle];
          if (!cellule.input.checked || cellule.input.disabled) return;
          var p = this.resoudre(cle);
          if (p && p.disponible) out.push({ cle: cle, proposition: p });
        }, this);
        return out;
      }

      // Le coffret que produiraient ces contributeurs (la fiche + des cochés), ou null : chaque
      // composant fixe présent (par id de variante), chaque option servie par exactement un
      // contributeur (par produit), aucun contributeur de reste, et la variante du coffret
      // (clé = teintes dans l'ordre des options, normalisées) présente et disponible.
      apparier(contributeurs) {
        var coffret = this.donnees.coffret;
        if (!coffret || !coffret.variantes) return null;
        var restants = contributeurs.slice();
        var fixes = coffret.fixes || [];
        for (var f = 0; f < fixes.length; f++) {
          var iFixe = -1;
          restants.forEach(function (x, i) { if (iFixe < 0 && x.id === fixes[f]) iFixe = i; });
          if (iFixe < 0) return null;
          restants.splice(iFixe, 1);
        }
        var cles = [];
        var options = coffret.options || [];
        for (var o = 0; o < options.length; o++) {
          var candidats = restants.filter(function (x) { return options[o].indexOf(x.produit) >= 0; });
          if (candidats.length !== 1) return null;
          cles.push(normaliser(candidats[0].teinte));
          restants.splice(restants.indexOf(candidats[0]), 1);
        }
        if (restants.length) return null;
        var cv = coffret.variantes[cles.join('|')];
        return cv && cv[3] ? { id: cv[0], prix: cv[1], barre: cv[2] } : null;
      }

      // Le coffret parmi les sous-ensembles des cochés (tous d'abord, puis chacun seul), la fiche
      // toujours dedans : rouge + crayon + gloss → le Duo Lèvres ET le gloss à l'unité.
      resoudreCoffret(fiche, coches) {
        if (!fiche || !this.donnees.coffret) return { coffret: null, utilises: [] };
        var sousEnsembles = [];
        if (coches.length > 1) sousEnsembles.push(coches);
        coches.forEach(function (c) { sousEnsembles.push([c]); });
        for (var i = 0; i < sousEnsembles.length; i++) {
          var contributeurs = [fiche].concat(sousEnsembles[i].map(function (c) { return c.proposition; }));
          var coffret = this.apparier(contributeurs);
          if (coffret) return { coffret: coffret, utilises: sousEnsembles[i].map(function (c) { return c.cle; }) };
        }
        return { coffret: null, utilises: [] };
      }

      // L'état : les lignes à ajouter au panier et le total, depuis les prix Shopify. Une case
      // cochée dont l'article est épuisé ne compte pas (recalculer() la décoche aussi).
      etat() {
        var v = this.variante();
        var fiche = v ? { id: parseInt(this.varianteId, 10), produit: this.donnees.fiche ? this.donnees.fiche.id : null, teinte: v.teinte, prix: v.prix } : null;
        var coches = this.coches();
        var resolu = this.resoudreCoffret(fiche, coches);
        var lignes = [], total = 0;
        // La quantité du formulaire (sélecteur natif, absent par défaut sur ce gabarit) porte sur
        // la fiche ou le coffret ; les articles cochés s'ajoutent à l'unité.
        var champQuantite = this.form ? this.form.querySelector('input[name="quantity"]') : null;
        var quantite = Math.max(1, parseInt(champQuantite && champQuantite.value, 10) || 1);
        if (resolu.coffret) {
          lignes.push({ id: resolu.coffret.id, quantity: quantite });
          total = resolu.coffret.prix * quantite;
        } else if (fiche) {
          lignes.push({ id: fiche.id, quantity: quantite });
          total = fiche.prix * quantite;
        }
        var restants = coches.filter(function (c) { return resolu.utilises.indexOf(c.cle) < 0; });
        restants.forEach(function (c) {
          lignes.push({ id: c.proposition.id, quantity: 1 });
          total += c.proposition.prix;
        });
        // Ce que la cliente paiera (chantier ⑮) : la remise étudiante de chaque produit
        // (`remiseEtudiant`, 0 hors étudiante connectée, jamais sur un kit ni sur Hôtel Charly :
        // cluutch-pdp-remise-etudiante.liquid) ; le coffret n'est jamais remisé. La jauge le compte.
        var prixPaye = function (prix, pourcent) { return pourcent ? Math.round(prix * (100 - pourcent) / 100) : prix; };
        var paye = resolu.coffret ? resolu.coffret.prix * quantite
          : (fiche ? prixPaye(fiche.prix, (this.donnees.fiche || {}).remiseEtudiant) * quantite : 0);
        restants.forEach(function (c) { paye += prixPaye(c.proposition.prix, c.proposition.remiseEtudiant); });
        return { lignes: lignes, total: total, paye: paye, coffret: resolu.coffret, utilises: resolu.utilises, coches: coches, restants: restants, personnalise: coches.length > 0 };
      }

      recalculer() {
        this.annoncesEpuise = [];
        var v = this.variante();

        // Chaque ligne cochable suit la teinte : la proposition résolue (produit, teinte, image,
        // prix), son titre (en fondu, seulement s'il change), sa disponibilité (épuisée, la case se
        // décoche et se désactive, l'annonce le dit).
        Object.keys(this.cases).forEach(function (cle) {
          var cellule = this.cases[cle];
          var p = this.resoudre(cle);
          var epuise = !p || !p.disponible;
          if (p) {
            var teinteFiche = v ? v.teinte : '';
            var titre = p.regle === 'assorti' && teinteFiche ? remplir(this.textes.addon_assorti, { nom: p.nom, teinte: teinteFiche })
              : (p.teinte ? remplir(this.textes.addon_teinte, { nom: p.nom, teinte: p.teinte }) : remplir(this.textes.addon_seul, { nom: p.nom }));
            if (cellule.titre && texteVise(cellule.titre) !== titre) fondre(cellule.titre, titre);
            var detail = p.titre + (p.regle === 'assorti' && p.teinte ? '\u00a0·\u00a0' + p.teinte : '');
            if (cellule.detail && cellule.detail.textContent !== detail) cellule.detail.textContent = detail;
            // la note du produit résolu (une par produit atteignable, rendues par Liquid)
            if (cellule.notes) {
              cellule.notes.forEach(function (note) { note.hidden = String(note.dataset.cluutchAddonNote) !== String(p.produit); });
            }
            if (cellule.prix) cellule.prix.textContent = remplir(this.textes.addon_plus, { montant: formatArgent(p.prix) });
            if (cellule.visuel) {
              var img = cellule.image;
              if (p.image) {
                if (!img) {
                  img = document.createElement('img');
                  img.className = 'cluutch-pdp__addon-image';
                  img.alt = '';
                  img.loading = 'lazy';
                  img.sizes = '46px';
                  cellule.visuel.appendChild(img);
                  cellule.image = img;
                }
                var srcset = p.image + ' 92w, ' + p.image2x + ' 184w';
                if (img.getAttribute('srcset') !== srcset) { img.srcset = srcset; img.src = p.image2x; }
              } else if (img) {
                img.remove();
                cellule.image = null;
              }
            }
          }
          if (epuise && cellule.input.checked) {
            cellule.input.checked = false;
            this.annoncesEpuise.push(remplir(this.textes.annonce_addon_epuise, { article: p ? p.nom : cle }));
          }
          cellule.input.disabled = epuise;
          cellule.label.classList.toggle('est-epuise', epuise);
        }, this);
        var etat = this.etat();

        // Le total, pour le bouton (cluutch-atc-price.js), le paiement fractionné et l'achat
        // flottant (lot 3), dont le prix n'est pas une cible du Section Rendering.
        if (this.productForm) this.productForm.setAttribute('data-cluutch-total', etat.total);
        this.racine.querySelectorAll('[data-cluutch-flottant-prix]').forEach(function (el) {
          el.textContent = formatArgent(etat.total);
          el.hidden = !!(v && v.disponible === false);
        });
        // Le prix barré du flottant et son tag « −9 % » (les coffrets, lot 9 et 21/09) : la clé
        // `barre` de la variante, masqués si le barré n'excède pas le total ou si une case est
        // cochée (le total n'est plus un prix catalogue). Le libellé caché « au lieu de » reste,
        // seul le montant est réécrit ; la rangée entière porte `hidden`.
        var barre = v && v.disponible !== false && v.barre > etat.total && !etat.personnalise ? v.barre : 0;
        var textes = this.textes;
        this.racine.querySelectorAll('[data-cluutch-flottant-reference]').forEach(function (rangee) {
          rangee.hidden = !barre;
          if (!barre) return;
          var el = rangee.querySelector('[data-cluutch-flottant-barre]');
          if (el) {
            var texte = el.lastChild && el.lastChild.nodeType === 3 ? el.lastChild : el.appendChild(document.createTextNode(''));
            texte.textContent = formatArgent(barre);
          }
          var tag = rangee.querySelector('[data-cluutch-flottant-remise]');
          if (tag) {
            var remiseFlottant = formatRemise(textes.remise, etat.total, barre);
            tag.textContent = remiseFlottant;
            tag.hidden = !remiseFlottant;
          }
        });
        document.dispatchEvent(new CustomEvent('cluutch:pdp-total', {
          detail: { sectionId: this.sectionId, total: etat.total, lignes: etat.lignes }
        }));

        this.peindreJauge(etat);

        // Le tag « −9 % » sur chaque ligne cochable : visible dès que cocher cet article, seul ou
        // avec l'autre, produirait un coffret moins cher que la somme (coché ou non) ; masqué
        // sinon (coffret sans remise, variante absente ou épuisée, article épuisé).
        var fiche = v ? { id: parseInt(this.varianteId, 10), produit: this.donnees.fiche ? this.donnees.fiche.id : null, teinte: v.teinte, prix: v.prix } : null;
        var cles = Object.keys(this.cases);
        cles.forEach(function (cle) {
          var cellule = this.cases[cle];
          if (!cellule.tag) return;
          var p = this.resoudre(cle);
          var remise = '';
          // Une ligne cochée ne garde le tag que si le coffret s'applique et l'emploie : cochée
          // au plein tarif (le mascara seul sur la palette du Trio), « −9 % » mentirait ; une
          // ligne décochée le porte dès que la cocher, seule ou avec l'autre, produirait le
          // coffret (audit du lot A).
          var cochee = cellule.input.checked;
          if (cochee && (!etat.coffret || etat.utilises.indexOf(cle) < 0)) {
            // rien
          } else if (p && p.disponible && fiche) {
            var essais = [[{ cle: cle, proposition: p }]];
            cles.forEach(function (autre) {
              var q = autre !== cle ? this.resoudre(autre) : null;
              if (q && q.disponible) essais.push([{ cle: cle, proposition: p }, { cle: autre, proposition: q }]);
            }, this);
            for (var i = 0; i < essais.length && !remise; i++) {
              var c = this.apparier([fiche].concat(essais[i].map(function (x) { return x.proposition; })));
              if (c) remise = formatRemise(this.textes.remise, c.prix, c.barre);
            }
          }
          var valeur = cellule.tag.querySelector('[data-cluutch-addon-remise-valeur]') || cellule.tag;
          valeur.textContent = remise;
          cellule.tag.hidden = !remise;
        }, this);

        // L'encart de bascule : visible quand un coffret remplace la fiche et des cochés. Son
        // texte et le détail du kit (image 36, titre, teinte · prix par contributeur) sont composés
        // depuis les données, tout texte échappé.
        var bascule = this.querySelector('[data-cluutch-bascule]');
        if (bascule) {
          var p = bascule.querySelector('[data-cluutch-bascule-texte]');
          var kit = bascule.querySelector('[data-cluutch-kit]');
          if (etat.coffret && p) {
            var coffretHtml = '<b>' + echapper(this.donnees.coffret.titre) + '</b>';
            var prix = formatArgent(etat.coffret.prix);
            var remiseCoffret = formatRemise(this.textes.remise, etat.coffret.prix, etat.coffret.barre);
            var texte = remiseCoffret
              ? remplir(this.textes.bascule_coffret, { coffret: coffretHtml, prix: prix, prix_barre: formatArgent(etat.coffret.barre), remise: echapper(remiseCoffret) })
              : remplir(this.textes.bascule_coffret_plein, { coffret: coffretHtml, prix: prix });
            if (etat.restants.length) {
              var autres = etat.restants.map(function (c) { return echapper(minuscule(c.proposition.nom)); }).join(this.textes.liste_et || ', ');
              // deux phrases : une espace entre elles, aucune après un point idéographique (zh-CN)
              var espace = /[\u3002\uff01\uff1f]$/.test(texte) ? '' : ' ';
              texte += espace + remplir(this.textes.bascule_avec, { autres: autres, total: formatArgent(etat.total) });
            }
            if (p.innerHTML !== texte) p.innerHTML = texte;
            if (kit) {
              var contributeurs = [{ titre: this.donnees.produit, teinte: v ? v.teinte : '', prix: v ? v.prix : 0, image72: v ? v.image72 : null }]
                .concat(etat.coches.filter(function (c) { return etat.utilises.indexOf(c.cle) >= 0; }).map(function (c) { return c.proposition; }));
              var html = contributeurs.map(function (x) {
                return '<span class="cluutch-pdp__kit-article" data-cluutch-spec="encart-bascule-article">'
                  + (x.image72 ? '<img src="' + echapper(x.image72) + '" width="38" height="38" alt="" loading="lazy">' : '')
                  + '<span><b>' + echapper(x.titre) + '</b><span>' + (x.teinte ? echapper(x.teinte) + ' · ' : '') + echapper(formatArgent(x.prix)) + '</span></span></span>';
              }).join('<span class="cluutch-pdp__kit-plus" aria-hidden="true">+</span>');
              if (kit.innerHTML !== html) kit.innerHTML = html;
            }
          }
          bascule.hidden = !etat.coffret;
        }
      }

      // Une ligne : le formulaire natif fait tout. Deux lignes ou le coffret : le moteur poste
      // lui-même, sur le même chemin que product.js (ProductForm.onSubmitHandler).
      surSoumission(evt) {
        // `form.id` n'est pas l'attribut : le formulaire contient `input[name="id"]`, et l'accès
        // nommé d'un formulaire rend cet input. L'attribut se lit par getAttribute.
        if (!evt.target || evt.target.getAttribute('id') !== this.formId) return;
        // La variante soumise est celle de l'input, à cet instant (product.js le tient à jour).
        this.varianteId = this.idCourant() || this.varianteId;
        // « Ajouté » n'attend qu'un ajout de CE formulaire : l'attente expire (échec, réseau).
        this.enAttente = true;
        window.clearTimeout(this.expiration);
        this.expiration = window.setTimeout(function () { this.enAttente = false; }.bind(this), 8000);
        var etat = this.etat();
        this.lignesSoumises = etat.lignes.map(function (l) { return String(l.id); });
        if (!etat.personnalise || !this.productForm) return;
        evt.preventDefault();
        evt.stopPropagation();
        if (!this.form.reportValidity()) return;

        var boutons = document.querySelectorAll('.single-add-to-cart-button');
        boutons.forEach(function (b) {
          if (b.classList.contains('loading')) return;
          b.setAttribute('aria-disabled', true);
          b.classList.add('loading');
        });
        this.productForm.handleErrorMessage();

        var sections = this.productForm.getSectionsToRender().map(function (s) { return s.section; }).join(',');
        fetch(window.theme.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
          body: JSON.stringify({ items: etat.lignes, sections: sections, sections_url: window.location.pathname })
        })
          .then(function (r) { return r.json().then(function (json) { if (!r.ok && !json.status) json.status = r.status; return json; }); })
          .then(function (json) {
            // Le chemin d'erreur de product.js, à l'identique : événement, rafraîchissement du
            // panier sur 422 (un article a pu changer), message sous le bouton.
            if (json.status) {
              this.enAttente = false;
              if (typeof dispatchCustomEvent === 'function') {
                dispatchCustomEvent('product:variant-error', { source: 'product-form', productVariantId: etat.lignes[0] && etat.lignes[0].id, errors: json.description, message: json.message });
              }
              if (json.status === 422) {
                document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
              }
              this.productForm.handleErrorMessage(json.description);
              return;
            }
            this.productForm.renderContents(json);
            if (typeof dispatchCustomEvent === 'function') {
              dispatchCustomEvent('cart:item-added', { product: json.items ? json.items[0] : json });
            } else {
              document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: json.items ? json.items[0] : json } }));
            }
          }.bind(this))
          .catch(function (e) { this.enAttente = false; console.error(e); }.bind(this))
          .finally(function () {
            boutons.forEach(function (b) {
              b.classList.remove('loading');
              b.removeAttribute('aria-disabled');
            });
          });
      }

      // « Ajouté » : fond succès et coche sur le bouton en ligne et sur celui de l'achat
      // flottant, 1 300 ms ; le tiroir s'ouvre par le thème.
      surAjout() {
        if (!this.enAttente) return;
        this.enAttente = false;
        window.clearTimeout(this.expiration);
        // La sélection est désormais dans le panier : elle ne compte plus dans la projection
        // jusqu'au prochain choix (teinte, case) ou jusqu'à ce qu'elle quitte le panier.
        this.selectionAjoutee = true;
        this.idsAjoutes = this.lignesSoumises || [];
        this.planifierPanier();
        var v = this.variante();
        this.annoncer(v && v.teinte
          ? remplir(this.textes.annonce_ajout_panier, { produit: this.donnees.produit, teinte: v.teinte })
          : remplir(this.textes.annonce_ajout_panier_seul, { produit: this.donnees.produit }));
        var libelle = this.dataset.libelleAjoute;
        var boutons = Array.prototype.slice.call(this.racine.querySelectorAll('.single-add-to-cart-button'));
        var avants = boutons.map(function (b) {
          var t = b.querySelector('.single-add-to-cart-button--text');
          var avant = t ? t.textContent : '';
          b.classList.add('est-ajoute');
          if (t && libelle) t.textContent = libelle;
          return avant;
        });
        window.clearTimeout(this.retour);
        this.retour = window.setTimeout(function () {
          boutons.forEach(function (b, i) {
            b.classList.remove('est-ajoute');
            var t = b.querySelector('.single-add-to-cart-button--text');
            if (t && (!libelle || t.textContent === libelle)) t.textContent = avants[i];
          });
        }, 1300);
      }

      // Un échec du chemin natif (product.js) : « Ajouté » ne doit pas suivre un ajout d'ailleurs.
      surEchec() {
        this.enAttente = false;
      }

      // La projection (chantier ⑯, 24/09) : le panier, plus la sélection dès qu'une case est cochée
      // (la fiche et les articles cochés, au prix que la cliente paiera, remise étudiante comprise).
      // Sans case cochée, la jauge ne montre que le panier : vide à l'arrivée, panier vide (retour
      // d'Hicham : « la barre est déjà à moitié remplie »). La sélection ne compte pas non plus quand
      // la teinte est épuisée, ni juste après son ajout : elle est alors dans le panier, elle
      // compterait deux fois.
      projection(etat) {
        var v = this.variante();
        var compte = etat.personnalise && !this.selectionAjoutee && v && v.disponible !== false;
        return this.panier + (compte ? etat.paye : 0);
      }

      // La jauge et la ligne de cadeau (chantier ⑯, 24/09). La largeur suit la projection, graduée
      // sur le dernier palier actif. Le vert (`est-pleine`) veut dire « livraison offerte acquise » :
      // il vient dès que la projection atteint le seuil de livraison, par le panier seul ou avec la
      // sélection cochée (retour d'Hicham : « ça ne devient pas vert quand on coche »). Les textes,
      // par priorité : le panier au-delà du palier (« Livraison offerte ») ; la projection au-delà
      // (« … avec cet achat ») ; rien de coché, mais la fiche seule au-delà du seuil, au prix payé :
      // « Livraison offerte avec cet achat », sans vert ni remplissage (Eternal Shadow et les
      // coffrets le disent dès l'arrivée) ; le reste (« Plus que 18€ ») ; rien du tout, la règle
      // (« Livraison offerte dès 50€ »). La même règle rend l'arrivée en Liquid
      // (cluutch-pdp-jauge.liquid). Ne réémet pas `cluutch:pdp-total`.
      peindreJauge(etat) {
        var seuils = this.donnees.seuils;
        var projection = this.projection(etat);
        var plafond = Math.max(seuils.livraison, seuils.cadeau || 0) || 1;
        var jauge = this.querySelector('[data-cluutch-jauge]');
        if (jauge) {
          var part = jauge.querySelector('[data-cluutch-jauge-part]');
          if (part) part.style.width = Math.min(100, (projection / plafond) * 100) + '%';
          jauge.classList.toggle('est-pleine', projection >= seuils.livraison);
          var texte = jauge.querySelector('[data-cluutch-jauge-texte]');
          if (texte) {
            var t = this.textes;
            var v = this.variante();
            var ficheSeule = !etat.personnalise && v && v.disponible !== false && etat.paye >= seuils.livraison;
            if (projection < seuils.livraison) {
              if (ficheSeule) texte.textContent = t.jauge_livraison_achat || '';
              else if (projection > 0) texte.textContent = remplir(t.jauge_reste_livraison, { montant: formatArgentCourt(seuils.livraison - projection) });
              else texte.textContent = remplir(t.jauge_livraison_des, { montant: formatArgentCourt(seuils.livraison) });
            }
            else if (seuils.cadeau > 0 && projection < seuils.cadeau) texte.textContent = remplir(t.jauge_reste_cadeau, { montant: formatArgentCourt(seuils.cadeau - projection) });
            else if (this.panier < plafond) texte.textContent = (seuils.cadeau > 0 ? t.jauge_cadeau_achat : t.jauge_livraison_achat) || '';
            else if (seuils.cadeau > 0) texte.textContent = t.jauge_cadeau || '';
            else texte.textContent = t.jauge_livraison || '';
          }
        }
        // La ligne de cadeau, au seuil.
        var cadeau = this.querySelector('[data-cluutch-cadeau]');
        if (cadeau) cadeau.hidden = !(seuils.cadeau > 0 && projection >= seuils.cadeau);
      }

      // Une rafale de changements (un re-rendu du tiroir, un ajout) : une seule relecture.
      planifierPanier() {
        window.clearTimeout(this.minuteriePanier);
        this.minuteriePanier = window.setTimeout(this.rafraichirPanier, 100);
      }

      // Le panier relu. La réponse de la dernière requête envoyée gagne ; un échec (réseau, 429 de
      // l'anti-robot) est ignoré, la jauge garde son état. La sélection ajoutée cesse de l'être dès
      // que l'une de ses lignes a quitté le panier (retirée dans le tiroir).
      rafraichirPanier() {
        var numero = this.requetePanier = (this.requetePanier || 0) + 1;
        fetch(window.theme.routes.cart_url + '.js', { cache: 'no-store', headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (panier) {
            if (!panier || numero !== this.requetePanier || !this.isConnected) return;
            var total = Number(panier.total_price) || 0;
            var ajoutee = this.selectionAjoutee;
            if (ajoutee) {
              var presents = (panier.items || []).map(function (l) { return String(l.variant_id); });
              this.selectionAjoutee = (this.idsAjoutes || []).every(function (id) { return presents.indexOf(id) >= 0; });
            }
            if (total === this.panier && ajoutee === this.selectionAjoutee) return;
            this.panier = total;
            this.peindreJauge(this.etat());
          }.bind(this))
          .catch(function () {});
      }

      surRetour(e) {
        if (e.persisted) this.rafraichirPanier();
      }

      annoncer(message) {
        if (!this.statut || !message) return;
        this.statut.textContent = '';
        window.setTimeout(function () { this.statut.textContent = message; }.bind(this), 50);
      }
    }

    customElements.define('cluutch-pdp-achat', CluutchPdpAchat);
  }

  /* ------------------------------------------------------------------ lot 9 : « Ce coffret contient » */
  /* Les lignes d'option du bloc de composition suivent la variante : la table JSON embarquée
     (par valeur d'option, cluutch-pdp-composition.liquid) donne le produit, la teinte, le prix
     et l'image du composant qui porte la valeur choisie ; le pied lit le prix et le prix barré
     de la variante. Filtré par section, comme le moteur. */
  if (!customElements.get('cluutch-pdp-composition')) {
    class CluutchPdpComposition extends HTMLElement {
      connectedCallback() {
        var brut = this.querySelector('[data-cluutch-composition-table]');
        try {
          this.table = brut ? JSON.parse(brut.textContent) : null;
        } catch (err) {
          this.table = null;
        }
        this.total = this.querySelector('[data-cluutch-composition-total]');
        this.surVariante = this.surVariante.bind(this);
        document.addEventListener('product:variant-change', this.surVariante);
      }

      disconnectedCallback() {
        document.removeEventListener('product:variant-change', this.surVariante);
      }

      surVariante(e) {
        if (!e.detail || !e.detail.variant) return;
        if (e.detail.sectionId !== this.dataset.section) return;
        var v = e.detail.variant;
        if (this.table) {
          this.querySelectorAll('[data-cluutch-composant="option"]').forEach(function (ligne) {
            var i = ligne.dataset.cluutchComposantOption;
            var valeur = v['option' + i] || '';
            var entree = this.table[i] ? this.table[i][normaliser(valeur)] : null;
            var detail = ligne.querySelector('.cluutch-pdp__composant-detail');
            if (detail) detail.textContent = entree ? entree.teinte : valeur;
            if (!entree) return;
            var titre = ligne.querySelector('.cluutch-pdp__composant-titre');
            if (titre) titre.textContent = entree.produit;
            var prix = ligne.querySelector('.cluutch-pdp__composant-prix');
            if (prix) prix.textContent = formatArgent(entree.prix);
            ligne.dataset.cluutchPrix = entree.prix;
            var visuel = ligne.querySelector('.cluutch-pdp__composant-visuel');
            var img = visuel ? visuel.querySelector('img') : null;
            if (visuel && entree.image) {
              if (!img) {
                img = document.createElement('img');
                img.className = 'cluutch-pdp__composant-image';
                img.alt = '';
                img.loading = 'lazy';
                img.sizes = '46px';
                visuel.appendChild(img);
              }
              img.srcset = entree.image + ' 92w, ' + entree.image2x + ' 184w';
              img.src = entree.image2x;
            } else if (img) {
              img.remove();
            }
          }, this);
        }
        // Le pied : le prix, le barré et le tag « −9 % » (modèle `data-cluutch-remise`).
        if (this.total) {
          var barreVisible = v.compare_at_price > v.price;
          var prixEl = this.total.querySelector('[data-cluutch-composition-prix]');
          if (prixEl) prixEl.textContent = formatArgent(v.price);
          var barreEl = this.total.querySelector('[data-cluutch-composition-barre]');
          if (barreEl) {
            barreEl.hidden = !barreVisible;
            var texteBarre = barreEl.lastChild && barreEl.lastChild.nodeType === 3 ? barreEl.lastChild : barreEl.appendChild(document.createTextNode(''));
            if (barreVisible) texteBarre.textContent = formatArgent(v.compare_at_price);
          }
          var tagEl = this.total.querySelector('[data-cluutch-composition-remise]');
          if (tagEl) {
            var remise = barreVisible ? formatRemise(this.dataset.cluutchRemise, v.price, v.compare_at_price) : '';
            tagEl.textContent = remise;
            tagEl.hidden = !remise;
          }
        }
      }
    }

    customElements.define('cluutch-pdp-composition', CluutchPdpComposition);
  }

  /* ------------------------------------------------------------------ l'habillage de collection */
  // <cluutch-pdp-habillage> (Hôtel Charly, 06/10/2026), rendu par snippets/cluutch-pdp-habillage-
  // donnees.liquid seulement sur une fiche dont une variante visible porte custom.habillage, avec
  // le JSON de ces variantes : `h` la poignée du métaobjet, `a` son accent, `c` la citation. Il suit
  // la teinte de SA section (l'aperçu rapide émet le même événement avec un autre `sectionId`) :
  // sur `<html>`, l'attribut `data-cluutch-habillage` et la variable `--cluutch-habillage-accent`
  // (l'état initial est posé par la section avant la colonne d'achat) ; l'encart du bloc
  // `cluutch_habillage` (affiché ou masqué, citation en fondu, posée sèche quand il réapparaît).
  // Un JSON illisible ne casse rien : l'élément ne fait alors rien.
  if (!customElements.get('cluutch-pdp-habillage')) {
    class CluutchPdpHabillage extends HTMLElement {
      connectedCallback() {
        var donnees = this.querySelector('script[type="application/json"]');
        try {
          this.variantes = JSON.parse(donnees ? donnees.textContent : '') || {};
        } catch (erreur) {
          return;
        }
        this.surVariante = this.surVariante.bind(this);
        document.addEventListener('product:variant-change', this.surVariante);
      }

      disconnectedCallback() {
        if (this.surVariante) document.removeEventListener('product:variant-change', this.surVariante);
      }

      surVariante(e) {
        if (!e.detail || !e.detail.variant || e.detail.sectionId !== this.dataset.section) return;
        var h = this.variantes[String(e.detail.variant.id)] || null;
        var racine = document.documentElement;
        if (h) racine.setAttribute('data-cluutch-habillage', h.h);
        else racine.removeAttribute('data-cluutch-habillage');
        if (h && h.a) racine.style.setProperty('--cluutch-habillage-accent', h.a);
        else racine.style.removeProperty('--cluutch-habillage-accent');

        var fiche = this.closest('.cluutch-pdp');
        var encart = fiche ? fiche.querySelector('[data-cluutch-habillage-encart]') : null;
        if (!encart) return;
        var reapparait = encart.hidden && !!h;
        encart.hidden = !h;
        var citation = encart.querySelector('[data-cluutch-habillage-citation]');
        if (!h || !citation) return;
        var texte = h.c ? remplir(encart.dataset.cluutchModele, { citation: h.c }) : '';
        citation.hidden = !texte;
        if (reapparait) {
          if (citation.cluutchFondu) { window.clearTimeout(citation.cluutchFondu); citation.cluutchFondu = null; }
          citation.classList.remove('est-en-fondu');
          citation.cluutchCible = null;
          citation.textContent = texte;
        } else if (texteVise(citation) !== texte) {
          fondre(citation, texte);
        }
      }
    }

    customElements.define('cluutch-pdp-habillage', CluutchPdpHabillage);
  }

  /* ------------------------------------------------------------------ lot 4 : les sections basses */
  // Le défilement d'un rail : `smooth` sauf sous prefers-reduced-motion (un `behavior` explicite
  // ne consulte pas la règle CSS).
  var douceur = function () {
    return mouvementReduit() ? 'auto' : 'smooth';
  };
  // Une section basse ne réagit qu'aux variantes de SON produit : l'aperçu rapide d'un autre
  // produit émet le même événement (`data-cluutch-variantes`, la liste des ids).
  var varianteDeLaFiche = function (el, e) {
    if (!e.detail || !e.detail.variant) return null;
    var ids = (el.dataset.cluutchVariantes || '').split(',');
    return ids.indexOf(String(e.detail.variant.id)) >= 0 ? e.detail.variant : null;
  };

  /* La barre d'ancres : suivi de lecture et pastille glissante (§E, §G ; lot 11). L'entrée active
     est la dernière dont la cible a passé le bas de la barre plus 8 px (en bas de page, la dernière
     dont la cible est à l'écran : une fiche courte ne fait pas passer sa dernière section sous la
     barre). La pastille noire (`[data-cluutch-pastille]`) prend la boîte du LIBELLÉ actif
     (`[data-cluutch-libelle]`, le span de 24 px dans l'entrée de 44) par `transform: translate(x, y)`
     et `width` ; sa piste (`[data-cluutch-pastille-piste]`, copie des entrées en blanc) reçoit la
     translation inverse dans la même passe, pour que la copie blanche reste sur l'original à
     chaque image : le texte devient blanc là où la pastille passe, et là seulement. Géométrie en
     rectangles (fractionnaire) : la pastille tombe sur la boîte exacte du libellé, la copie sur le
     texte exact de l'original ; des offsets entiers décentreraient l'encre d'un pixel.

     La transition (ressort de 420 ms, CSS) ne joue que sous `est-prete`, armée au REPOS du
     défilement après la première pose et l'arrivée des polices : ni jaillissement au chargement,
     ni glissement pendant un défilement que le navigateur fait seul (fragment `#…` à l'ouverture,
     restauration de position) ; un recalage sec au redimensionnement (largeur seule : la barre
     d'outils d'un téléphone qui se replie émet aussi `resize`).

     Le dépassement du ressort est une part de la course (5,5 %, `--cluutch-ressort`) : sur une
     course longue il dépasserait le remplissage de la pastille (6 px + .14em) et le bord noir
     rognerait la première lettre de la copie, qui ne bouge pas. Au-delà de 5 px de dépassement, le
     module calcule la courbe pour la course (même formule que docs/refonte-pdp/ressort.py,
     plafond 5 px) et la pose en ligne le temps du mouvement, mesurée depuis la position COURANTE
     quand un ressort est en vol ; la largeur suit la courbe charte, sans rebond. Sans `linear()`
     (Safari avant 17.2), une course longue prend la courbe charte : pas de rebond plutôt qu'un
     rebond qui rogne. Un clic sur un lien d'ancre (une entrée de la barre, ou tout `#cible`
     visé par une entrée, comme « Lire les avis ») envoie la pastille droit sur l'entrée, d'un seul
     ressort, sans passer par chaque entrée intermédiaire que le suivi activerait pendant le
     défilement doux ; le suivi reprend au repos du défilement. Les entrées sans cible dans la page
     (section masquée) sont retirées avec leur copie, aux mêmes indices ; moins de deux entrées,
     la barre se masque. La géométrie se lit une fois par image de défilement. */
  var RESSORT_DEPASSEMENT = 0.055;   // part de la course au pic (le jeton --cluutch-ressort)
  var RESSORT_PIC = 0.58;            // instant du pic, part de la durée
  var RESSORT_PLAFOND = 5;           // dépassement maximal en pixels, sous le remplissage de 6 px
  var RESSORT_EN_LIGNE = 'CSS' in window && typeof CSS.supports === 'function' && CSS.supports('transition-timing-function', 'linear(0, 1)');
  var REPOS_DEFILEMENT = 200;        // ms sans événement scroll : le défilement est au repos
  var courbesRessort = {};
  // Le centième de pixel : assez fin pour l'encre, assez court pour comparer deux styles.
  var arrondir = function (v) { return Math.round(v * 100) / 100; };
  // La réponse d'un ressort amorti pour un dépassement donné, en `linear()` : ζ depuis le
  // dépassement, ω depuis l'instant du pic, 28 arrêts resserrés sur la montée (ressort.py).
  var courbeRessort = function (depassement) {
    var cle = String(Math.round(depassement * 1000));
    if (courbesRessort[cle]) return courbesRessort[cle];
    var ln = Math.log(depassement);
    var zeta = -ln / Math.sqrt(Math.PI * Math.PI + ln * ln);
    var racine = Math.sqrt(1 - zeta * zeta);
    var omega = Math.PI / (RESSORT_PIC * racine);
    var arrets = [];
    for (var i = 0; i <= 14; i++) arrets.push(i * 0.025);
    for (var j = 0; j <= 12; j++) arrets.push(0.4 + j * 0.05);
    var parts = arrets.map(function (t) {
      var x = t === 0 ? 0 : (t >= 1 ? 1 : 1 - Math.exp(-zeta * omega * t) * (Math.cos(omega * racine * t) + zeta / racine * Math.sin(omega * racine * t)));
      return (Math.round(x * 1000) / 1000) + ' ' + (Math.round(t * 1000) / 10) + '%';
    });
    return (courbesRessort[cle] = 'linear(' + parts.join(', ') + ')');
  };

  if (!customElements.get('cluutch-pdp-ancres')) {
    class CluutchPdpAncres extends HTMLElement {
      connectedCallback() {
        // Les indices sont relevés AVANT tout retrait : une entrée morte emporte sa copie.
        var liens = Array.prototype.slice.call(this.querySelectorAll('[data-cluutch-ancre]'));
        var copies = Array.prototype.slice.call(this.querySelectorAll('[data-cluutch-copie]'));
        this.entrees = [];
        this.copies = [];
        liens.forEach(function (a, i) {
          var id = (a.getAttribute('href') || '').replace(/^#/, '');
          var cible = id ? document.getElementById(id) : null;
          if (cible) {
            this.entrees.push(a);
            this.copies.push(copies[i] || null);
          } else {
            a.remove();
            if (copies[i]) copies[i].remove();
          }
        }, this);
        this.enveloppe = this.closest('.shopify-section') || this;
        if (this.entrees.length < 2) {
          this.enveloppe.hidden = true;
          return;
        }
        this.barre = this.querySelector('nav');
        this.pastille = this.querySelector('[data-cluutch-pastille]');
        this.piste = this.querySelector('[data-cluutch-pastille-piste]');
        this.actif = -1;
        this.verrou = -1;
        this.poseX = null;
        this.largeurBarre = this.barre ? this.barre.clientWidth : 0;
        this.attente = false;
        this.surDefilement = this.surDefilement.bind(this);
        this.surRedimension = this.surRedimension.bind(this);
        this.surClic = this.surClic.bind(this);
        this.surDefilementBarre = this.surDefilementBarre.bind(this);
        window.addEventListener('scroll', this.surDefilement, { passive: true });
        window.addEventListener('resize', this.surRedimension);
        if (this.barre) this.barre.addEventListener('scroll', this.surDefilementBarre, { passive: true });
        // Le clic sur un lien d'ancre, où qu'il soit dans la page : en capture, avant l'action par
        // défaut (le défilement doux du thème).
        document.addEventListener('click', this.surClic, true);
        // `--header-height` (lu par le `top` de la barre et le scroll-margin-top des sections)
        // est publié par header.js à chaque défilement, depuis le clientHeight de <theme-header>,
        // AVANT que la transition de hauteur de l'en-tête (83 → 61 sur téléphone) ne finisse :
        // un retour en haut de page la laisse à 60 quand l'en-tête mesure 83, et le saut d'ancre
        // suivant atterrit 22 px trop bas. La même mesure, republiée quand l'en-tête a fini de
        // changer de taille (et au redimensionnement, que header.js n'écoute pas).
        var entete = document.querySelector('theme-header');
        if (entete && 'ResizeObserver' in window) {
          this.mesureEntete = new ResizeObserver(function () {
            document.documentElement.style.setProperty('--header-height', entete.clientHeight + 'px');
          });
          this.mesureEntete.observe(entete);
        }
        // Première pose sèche, puis recalage quand DIN Pro est là (`font-display: swap` : les
        // largeurs mesurées avant son arrivée sont celles de la police de repli), et le ressort
        // armé au repos du défilement.
        this.caler();
        var self = this;
        (document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve()).then(function () {
          if (self.isConnected) self.poserSec();
        });
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.surDefilement);
        window.removeEventListener('resize', this.surRedimension);
        document.removeEventListener('click', this.surClic, true);
        if (this.barre) this.barre.removeEventListener('scroll', this.surDefilementBarre);
        window.clearTimeout(this.verrouFin);
        window.clearTimeout(this.verrouMax);
        window.clearTimeout(this.armement);
        if (this.mesureEntete) this.mesureEntete.disconnect();
      }

      surDefilement() {
        // Un verrou de clic ou un armement en attente se règlent au repos du défilement.
        if (this.verrou >= 0) this.programmerFinVerrou();
        if (this.armement) this.armer();
        if (this.attente) return;
        this.attente = true;
        window.requestAnimationFrame(function () {
          this.attente = false;
          this.caler();
        }.bind(this));
      }

      surRedimension() {
        var largeur = this.barre ? this.barre.clientWidth : 0;
        if (largeur === this.largeurBarre) return;   // hauteur seule : la barre d'outils du téléphone
        this.largeurBarre = largeur;
        this.poserSec();
      }

      // Le voile de bord du rail (téléphone, dix entrées dans 390 px) : `data-cluutch-deborde`
      // dit de quel côté la barre continue (« gauche », « droite », les deux), la feuille pose le
      // dégradé. Relu à chaque défilement de la barre (une image), à chaque calage, au
      // redimensionnement ; rien quand tout tient.
      surDefilementBarre() {
        if (this.attenteBarre) return;
        this.attenteBarre = true;
        window.requestAnimationFrame(function () {
          this.attenteBarre = false;
          this.marquerDebord();
        }.bind(this));
      }

      marquerDebord() {
        if (!this.barre) return;
        var max = this.barre.scrollWidth - this.barre.clientWidth;
        var gauche = this.barre.scrollLeft;
        var cotes = [];
        if (max > 1 && gauche > 1) cotes.push('gauche');
        if (max > 1 && gauche < max - 1) cotes.push('droite');
        var valeur = cotes.join(' ');
        if (valeur) {
          if (this.barre.getAttribute('data-cluutch-deborde') !== valeur) this.barre.setAttribute('data-cluutch-deborde', valeur);
        } else if (this.barre.hasAttribute('data-cluutch-deborde')) {
          this.barre.removeAttribute('data-cluutch-deborde');
        }
      }

      surClic(e) {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        var a = e.target.closest ? e.target.closest('a[href^="#"]') : null;
        if (!a) return;
        var href = a.getAttribute('href');
        var i = -1;
        this.entrees.forEach(function (entree, j) { if (entree.getAttribute('href') === href) i = j; });
        if (i < 0) return;
        this.verrou = i;
        window.clearTimeout(this.verrouMax);
        this.verrouMax = null;
        this.programmerFinVerrou();
        this.caler();
      }

      // Le verrou tombe 200 ms après le dernier événement de défilement (ou tout de suite si rien
      // ne défile), jamais plus tard que 3 s : un défilement doux long en prend moins.
      programmerFinVerrou() {
        window.clearTimeout(this.verrouFin);
        this.verrouFin = window.setTimeout(function () {
          this.verrou = -1;
          this.caler();
        }.bind(this), REPOS_DEFILEMENT);
        if (!this.verrouMax) {
          this.verrouMax = window.setTimeout(function () {
            this.verrouMax = null;
            window.clearTimeout(this.verrouFin);
            this.verrou = -1;
            this.caler();
          }.bind(this), 3000);
        }
      }

      // Pose sans transition : `est-prete` retirée, la géométrie posée et le style FIGÉ par une
      // lecture de mise en page (sans elle, la classe remise dans la même passe de style ferait
      // partir une transition depuis l'ancienne pose), puis le ressort réarmé au repos.
      poserSec() {
        this.classList.remove('est-prete');
        this.caler();
        if (this.pastille) void this.pastille.offsetWidth;
        this.armer();
      }

      armer() {
        window.clearTimeout(this.armement);
        this.armement = window.setTimeout(function () {
          this.armement = null;
          if (this.isConnected) this.classList.add('est-prete');
        }.bind(this), REPOS_DEFILEMENT);
      }

      caler() {
        // Le seuil : le bas de la barre (collée ou non, son rectangle le dit) plus 8 px. Les
        // cibles se relisent à chaque calage : l'éditeur de thème re-rend une section (nouvel
        // élément, même id), et un élément détaché mesure zéro.
        var seuil = this.getBoundingClientRect().bottom + 8;
        var enBas = window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 1;
        var actif = 0;
        this.entrees.forEach(function (a, i) {
          var cible = document.getElementById(a.getAttribute('href').replace(/^#/, ''));
          if (!cible) return;
          var haut = cible.getBoundingClientRect().top;
          if (haut <= seuil || (enBas && haut < window.innerHeight)) actif = i;
        });
        if (this.verrou >= 0) actif = this.verrou;
        var change = actif !== this.actif, premiere = this.actif === -1;
        if (change) {
          this.actif = actif;
          this.entrees.forEach(function (a, i) {
            if (i === actif) a.setAttribute('aria-current', 'true');
            else a.removeAttribute('aria-current');
          });
        }
        var vise = this.entrees[actif];
        var libelle = vise ? vise.querySelector('[data-cluutch-libelle]') : null;
        if (!this.pastille || !this.barre || !libelle) return;
        // Toutes les lectures avant toute écriture (une seule mise en page). Le libellé se mesure
        // dans le repère de la boîte de remplissage de la barre (l'origine du calque absolu) :
        // rectangle moins la bordure, plus le défilement horizontal de la barre. La copie se
        // mesure dans le repère de la piste, quelle que soit la translation de celle-ci.
        var copie = this.copies[actif];
        var copieLibelle = copie ? copie.querySelector('[data-cluutch-libelle]') : null;
        var nr = this.barre.getBoundingClientRect(), lr = libelle.getBoundingClientRect();
        var x = lr.left - nr.left - this.barre.clientLeft + this.barre.scrollLeft;
        var y = lr.top - nr.top - this.barre.clientTop;
        var largeur = lr.width;
        var contre = null;
        if (this.piste && copieLibelle) {
          var pr = this.piste.getBoundingClientRect(), cr = copieLibelle.getBoundingClientRect();
          contre = 'translate(' + arrondir(pr.left - cr.left) + 'px, ' + arrondir(pr.top - cr.top) + 'px)';
        }
        var deborde = change && !premiere && this.barre.scrollWidth > this.barre.clientWidth;
        var gaucheEntree = vise.offsetLeft, largeurEntree = vise.offsetWidth, defilement = this.barre.scrollLeft, visible = this.barre.clientWidth;
        var pose = 'translate(' + arrondir(x) + 'px, ' + arrondir(y) + 'px)';
        if (this.pastille.style.transform !== pose) {
          // La courbe du mouvement : le jeton, sauf si son dépassement (5,5 % de la course)
          // excéderait le plafond ; alors la courbe calculée pour la course, mesurée depuis la
          // position courante si un ressort est en vol, posée en ligne.
          var courbe = '';
          if (this.poseX !== null) {
            var depart = this.poseX;
            if (this.pastille.getAnimations && this.pastille.getAnimations().length) {
              var m = getComputedStyle(this.pastille).transform.match(/matrix\(([^)]+)\)/);
              if (m) depart = parseFloat(m[1].split(',')[4]);
            }
            var course = Math.abs(x - depart);
            if (course * RESSORT_DEPASSEMENT > RESSORT_PLAFOND) courbe = RESSORT_EN_LIGNE ? courbeRessort(RESSORT_PLAFOND / course) : 'var(--cluutch-courbe)';
          }
          this.pastille.style.transitionTimingFunction = courbe ? courbe + ', var(--cluutch-courbe)' : '';
          if (this.piste) this.piste.style.transitionTimingFunction = courbe;
          this.pastille.style.transform = pose;
          this.poseX = x;
        }
        var l = arrondir(largeur) + 'px';
        if (this.pastille.style.width !== l) this.pastille.style.width = l;
        if (contre && this.piste.style.transform !== contre) this.piste.style.transform = contre;
        // Une barre qui déborde (téléphone étroit) amène l'entrée active en vue, SEULEMENT quand
        // elle change : à chaque image, un pouce qui fait défiler la barre serait combattu.
        if (deborde && (gaucheEntree < defilement || gaucheEntree + largeurEntree > defilement + visible)) {
          this.barre.scrollTo({ left: Math.max(0, gaucheEntree - (visible - largeurEntree) / 2), behavior: douceur() });
        }
        this.marquerDebord();
      }
    }

    customElements.define('cluutch-pdp-ancres', CluutchPdpAncres);
  }

  /* Le rail des teintes (§G, « ce qui suit la teinte ») : la carte de la teinte choisie porte
     `aria-current` et le rail se recale sur elle (jamais la fenêtre) ; cliquer une carte coche la
     radio du sélecteur principal et émet `change`, variant-selects fait le reste. Chevrons sur
     ordinateur, masqués aux extrémités. */
  if (!customElements.get('cluutch-pdp-teintes')) {
    class CluutchPdpTeintes extends HTMLElement {
      connectedCallback() {
        this.rail = this.querySelector('[data-cluutch-rail]');
        this.cartes = Array.prototype.slice.call(this.querySelectorAll('[data-cluutch-teinte]'));
        this.prec = this.querySelector('[data-cluutch-prec]');
        this.suiv = this.querySelector('[data-cluutch-suiv]');
        if (!this.rail) return;
        this.surVariante = this.surVariante.bind(this);
        this.borner = this.borner.bind(this);
        document.addEventListener('product:variant-change', this.surVariante);
        this.cartes.forEach(function (carte) {
          carte.addEventListener('click', function () { this.choisir(carte); }.bind(this));
        }.bind(this));
        if (this.prec) this.prec.addEventListener('click', function () { this.rail.scrollBy({ left: -this.pas(), behavior: douceur() }); }.bind(this));
        if (this.suiv) this.suiv.addEventListener('click', function () { this.rail.scrollBy({ left: this.pas(), behavior: douceur() }); }.bind(this));
        this.rail.addEventListener('scroll', this.borner, { passive: true });
        // Le rendu initial : mesures nulles tant que la section n'est pas mise en page.
        if ('ResizeObserver' in window) new ResizeObserver(this.borner).observe(this.rail);
        this.borner();
        var courante = this.cartes.filter(function (c) { return c.hasAttribute('aria-current'); })[0];
        if (courante) this.recaler(courante, 'auto');
      }

      disconnectedCallback() {
        document.removeEventListener('product:variant-change', this.surVariante);
      }

      pas() {
        var c = this.rail.children;
        return c.length > 1 ? c[1].offsetLeft - c[0].offsetLeft : this.rail.clientWidth;
      }

      borner() {
        var reste = this.rail.scrollWidth - this.rail.clientWidth;
        if (this.prec) this.prec.hidden = this.rail.scrollLeft < 4;
        if (this.suiv) this.suiv.hidden = reste < 4 || this.rail.scrollLeft > reste - 4;
      }

      recaler(carte, comportement) {
        this.rail.scrollTo({ left: carte.offsetLeft, behavior: comportement || douceur() });
      }

      choisir(carte) {
        var option = this.dataset.cluutchOption || '';
        var radio = null;
        var radios = document.querySelectorAll('.cluutch-pdp__achat variant-selects input[type="radio"]');
        Array.prototype.forEach.call(radios, function (r) {
          if (!radio && r.name === option && r.value === carte.dataset.cluutchTeinte) radio = r;
        });
        if (!radio) {
          // pas de sélecteur à radios dans la page (gabarit sans la fiche, sélecteur en liste) :
          // la fiche de la variante, plutôt qu'une carte qui se dirait choisie pour rien
          var url = new URL(window.location.href);
          url.searchParams.set('variant', carte.dataset.cluutchVariante);
          window.location.assign(url.toString());
          return;
        }
        if (radio.checked) return;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }

      marquer(carte) {
        this.cartes.forEach(function (c) {
          if (c === carte) c.setAttribute('aria-current', 'true');
          else c.removeAttribute('aria-current');
        });
      }

      surVariante(e) {
        var v = varianteDeLaFiche(this, e);
        if (!v) return;
        var carte = this.cartes.filter(function (c) { return c.dataset.cluutchVariante === String(v.id); })[0];
        // une variante sans carte (sans image) : aucune carte ne se dit choisie
        this.marquer(carte || null);
        if (carte) this.recaler(carte);
      }
    }

    customElements.define('cluutch-pdp-teintes', CluutchPdpTeintes);
  }

  /* Le comparateur avant / après : la largeur du volet et la position de la poignée suivent le
     pointeur (sans transition) ; la poignée est un curseur clavier (± 5 %) ; la photo « après »
     est celle de la teinte choisie, commutée en fondu (§G). */
  if (!customElements.get('cluutch-pdp-compare')) {
    class CluutchPdpCompare extends HTMLElement {
      connectedCallback() {
        this.volet = this.querySelector('[data-cluutch-volet]');
        this.poignee = this.querySelector('[data-cluutch-poignee]');
        this.libelle = this.querySelector('[data-cluutch-libelle-apres]');
        if (!this.volet || !this.poignee) return;
        this.pc = 50;
        this.surVariante = this.surVariante.bind(this);
        document.addEventListener('product:variant-change', this.surVariante);
        this.addEventListener('pointerdown', function (e) {
          if (e.button !== 0) return;
          this.depuisX(e.clientX);
          try { this.setPointerCapture(e.pointerId); } catch (err) { /* un pointeur déjà relâché */ }
        }.bind(this));
        this.addEventListener('pointermove', function (e) {
          if (e.buttons) this.depuisX(e.clientX);
        }.bind(this));
        this.poignee.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowLeft') { this.poser(this.pc - 5); e.preventDefault(); }
          if (e.key === 'ArrowRight') { this.poser(this.pc + 5); e.preventDefault(); }
          if (e.key === 'Home') { this.poser(0); e.preventDefault(); }
          if (e.key === 'End') { this.poser(100); e.preventDefault(); }
        }.bind(this));
        this.poser(50);
      }

      disconnectedCallback() {
        document.removeEventListener('product:variant-change', this.surVariante);
      }

      depuisX(x) {
        var r = this.getBoundingClientRect();
        if (!r.width) return;
        this.poser((x - r.left) / r.width * 100);
      }

      poser(valeur) {
        this.pc = Math.max(0, Math.min(100, valeur));
        this.volet.style.width = this.pc + '%';
        this.poignee.style.left = this.pc + '%';
        var arrondi = Math.round(this.pc);
        this.poignee.setAttribute('aria-valuenow', arrondi);
        this.poignee.setAttribute('aria-valuetext', remplir(this.dataset.cluutchModeleValeur, { pourcent: arrondi }));
      }

      surVariante(e) {
        var v = varianteDeLaFiche(this, e);
        if (!v) return;
        var cible = this.querySelector('[data-cluutch-variante="' + v.id + '"]');
        // une variante sans photo « après » : la photo et son libellé restent ceux qui sont
        // affichés (rien d'inventé : pas de photo d'une teinte étiquetée d'une autre)
        if (!cible) return;
        // un changement qui en rattrape un autre (deux cellules en moins de 160 ms) : le fondu
        // en cours est abandonné, l'état repart d'une seule photo visible
        if (this.fondu) {
          window.clearTimeout(this.fondu);
          this.fondu = null;
        }
        var toutes = Array.prototype.slice.call(this.querySelectorAll('.cluutch-pdp-compare__apres'));
        toutes.forEach(function (a) { a.classList.remove('est-en-fondu'); });
        var courante = toutes.filter(function (a) { return !a.hidden; })[0] || null;
        if (this.libelle) this.libelle.textContent = remplir(this.dataset.cluutchModeleApres, { teinte: v.option1 || '' });
        var commuter = function () {
          toutes.forEach(function (a) { a.hidden = a !== cible; });
        };
        if (cible === courante) { commuter(); return; }
        if (mouvementReduit() || !courante) { commuter(); return; }
        courante.classList.add('est-en-fondu');
        this.fondu = window.setTimeout(function () {
          this.fondu = null;
          courante.classList.remove('est-en-fondu');
          commuter();
          // le fondu d'entrée : un style calculé avec l'opacité à 0 (la lecture force la mise en
          // page), puis la transition vers 1
          cible.classList.add('est-en-fondu');
          void cible.offsetWidth;
          cible.classList.remove('est-en-fondu');
        }.bind(this), 160);
      }
    }

    customElements.define('cluutch-pdp-compare', CluutchPdpCompare);
  }

  /* Les avis : « Lire les N avis » déplie le widget complet (bloc d'app), et les photos d'avis
     s'ouvrent dans la loupe (<dialog>), comme celles de la galerie. */
  if (!customElements.get('cluutch-pdp-avis')) {
    class CluutchPdpAvis extends HTMLElement {
      connectedCallback() {
        this.lire = this.querySelector('[data-cluutch-avis-lire]');
        this.complets = this.querySelector('[data-cluutch-complets]');
        this.zoom = this.querySelector('[data-cluutch-zoom]');
        if (this.lire && this.complets) {
          this.lire.dataset.cluutchLibelleFerme = this.lire.textContent;
          this.lire.addEventListener('click', this.basculer.bind(this));
        }
        if (this.zoom) {
          var fermer = this.zoom.querySelector('[data-cluutch-zoom-fermer]');
          if (fermer) fermer.addEventListener('click', function () { this.zoom.close(); }.bind(this));
          this.zoom.addEventListener('click', function (e) { if (e.target === this.zoom) this.zoom.close(); }.bind(this));
          this.querySelectorAll('[data-cluutch-photo]').forEach(function (bouton) {
            bouton.addEventListener('click', function () { this.ouvrir(bouton); }.bind(this));
          }.bind(this));
        }
      }

      basculer() {
        var ouvert = this.complets.hidden;
        this.complets.hidden = !ouvert;
        this.lire.setAttribute('aria-expanded', ouvert ? 'true' : 'false');
        this.lire.textContent = ouvert ? (this.lire.dataset.cluutchLibelleOuvert || this.lire.textContent) : this.lire.dataset.cluutchLibelleFerme;
        if (ouvert) this.complets.focus({ preventScroll: true });
      }

      ouvrir(bouton) {
        var cible = this.zoom.querySelector('[data-cluutch-zoom-image]');
        if (!cible) return;
        cible.src = bouton.dataset.cluutchPhoto;
        cible.alt = bouton.dataset.cluutchPhotoAlt || '';
        this.zoom.showModal();
      }
    }

    customElements.define('cluutch-pdp-avis', CluutchPdpAvis);
  }

  /* ------------------------------------------------------------------ lot 5 : le mouvement (§G) */
  // Sous prefers-reduced-motion rien ne bouge : ni révélation (les sections sont visibles
  // d'emblée, la classe qui les masque n'est pas posée), ni amorce.
  if (!mouvementReduit() && 'IntersectionObserver' in window) {
    document.documentElement.classList.add('cluutch-pdp-anime');

    // Les révélations : entrée à 12 % dans l'écran, opacité (sur la section) et translateY(12px → 0)
    // (sur ses enfants : la section est une cible d'ancre, sa boîte ne doit pas bouger) en CSS,
    // aucune cascade (refusée en v12). Un saut d'ancre fait entrer la section : l'observateur
    // la révèle comme les autres.
    var revelateur = new IntersectionObserver(function (entrees) {
      entrees.forEach(function (en) {
        if (!en.isIntersecting) return;
        en.target.classList.add('est-revele');
        revelateur.unobserve(en.target);
      });
    }, { threshold: 0.12 });

    // L'amorce de défilement (mesurée chez Refy, 08/09) : 520 ms après l'entrée du rail à 60 %,
    // une seule fois par rail, le rail se déplace VRAIMENT de 40 % d'une lame depuis son repos
    // (un rail recalé sur la teinte choisie n'est pas remis à zéro) et revient, 980 ms, 55 %
    // aller / 45 % retour, sur la courbe de charte ; le défilement aimanté est suspendu le temps
    // de l'amorce, sinon chaque écriture de scrollLeft re-snappe. Coupée au premier geste sur le
    // rail, qu'il tombe pendant le mouvement ou dans les 520 ms qui le précèdent (les écouteurs
    // sont armés dès l'observation, et un rail qui a bougé depuis son entrée n'est pas amorcé) ;
    // coupée aussi par un changement de teinte : la galerie et le rail des teintes se recalent
    // alors d'un défilement doux, qu'une écriture de scrollLeft annulerait.
    var COURBE = function (t) { return 1 - Math.pow(1 - t, 3); };   // approche cubic-bezier(.22,.61,.36,1)
    var etats = new Map();   // rail -> { touche, repos, couper }
    var toucher = function (rail) {
      var e = etats.get(rail);
      if (!e) return;
      e.touche = true;
      if (e.couper) e.couper();
    };
    var amorcer = function (rail) {
      var e = etats.get(rail);
      if (!e || e.touche || Math.abs(rail.scrollLeft - e.repos) > 1) return;
      var lames = rail.children;
      var pas = lames.length > 1 ? lames[1].offsetLeft - lames[0].offsetLeft : 0;
      // rien à amorcer sur un rail qui ne déborde pas (grille des recos sur ordinateur)
      if (!pas || rail.scrollWidth - rail.clientWidth < 8) return;
      var repos = rail.scrollLeft;
      var ampli = Math.min(Math.round(pas * 0.4), rail.scrollWidth - rail.clientWidth - repos);
      if (ampli <= 0) return;
      var DUREE = 980, depart = null, coupe = false;
      var snap = rail.style.scrollSnapType;
      rail.style.scrollSnapType = 'none';
      // coupée : plus aucune écriture de scrollLeft, le geste ou le recalage garde la main
      e.couper = function () { coupe = true; rail.style.scrollSnapType = snap; e.couper = null; };
      var etape = function (t) {
        if (coupe) return;
        if (depart === null) depart = t;
        var a = Math.min(1, (t - depart) / DUREE);
        var d = a < 0.55 ? COURBE(a / 0.55) : 1 - COURBE((a - 0.55) / 0.45);
        rail.scrollLeft = repos + ampli * d;
        if (a < 1) { window.requestAnimationFrame(etape); return; }
        rail.scrollLeft = repos;
        rail.style.scrollSnapType = snap;
        e.couper = null;
      };
      window.requestAnimationFrame(etape);
    };
    var amorceur = new IntersectionObserver(function (entrees) {
      entrees.forEach(function (en) {
        var rail = en.target, e = etats.get(rail);
        if (!en.isIntersecting || !e || e.touche || rail.dataset.cluutchAmorce === 'faite') return;
        rail.dataset.cluutchAmorce = 'faite';
        amorceur.unobserve(rail);
        e.repos = rail.scrollLeft;
        window.setTimeout(function () { amorcer(rail); }, 520);
      });
    }, { threshold: 0.6 });
    var armer = function (rail) {
      if (etats.has(rail)) return;
      etats.set(rail, { touche: false, repos: rail.scrollLeft, couper: null });
      ['pointerdown', 'wheel', 'touchstart', 'keydown'].forEach(function (nom) {
        rail.addEventListener(nom, function () { toucher(rail); }, { once: true, passive: true });
      });
      amorceur.observe(rail);
    };
    document.addEventListener('product:variant-change', function () {
      etats.forEach(function (e, rail) { if (rail.hasAttribute('data-cluutch-rail')) toucher(rail); });
    });

    var observer = function (racine) {
      racine.querySelectorAll('[data-cluutch-reveal]').forEach(function (el) { revelateur.observe(el); });
      racine.querySelectorAll('[data-cluutch-amorce]').forEach(armer);
    };
    observer(document);
    // « Complétez votre look » sans compléments : <product-recommendations> est rempli par
    // product-recommendations.js après un fetch, sans événement, toujours après ce module. Ce
    // qu'il injecte (la section et son rail) est observé à son tour, sinon la section resterait à
    // opacité 0.
    document.querySelectorAll('product-recommendations.cluutch-pdp-recos__differe').forEach(function (pr) {
      new MutationObserver(function () { observer(pr); }).observe(pr, { childList: true });
    });
  }

  // La vidéo de L'actif (autoplay muet en boucle) ne joue qu'à l'écran ; sous prefers-reduced-
  // motion elle reste sur son affiche.
  if ('IntersectionObserver' in window) {
    var videos = document.querySelectorAll('.cluutch-pdp-actif video');
    if (videos.length) {
      var lecteur = new IntersectionObserver(function (entrees) {
        entrees.forEach(function (en) {
          var v = en.target;
          if (mouvementReduit()) { v.pause(); return; }
          if (en.intersectionRatio >= 0.5) v.play().catch(function () {}); else v.pause();
        });
      }, { threshold: [0, 0.5] });
      videos.forEach(function (v) { lecteur.observe(v); });
    }
  }
}
